/**
 * The formatting the expanded composer writes, and how it is written down.
 *
 * Messages stay plain strings end to end — one `text` field, the same one the
 * BFF and Stream already carry — with a small markdown subset as the convention
 * for what is bold, struck, italic, listed or linked. No HTML is stored or sent,
 * so a message can never smuggle markup into the page, and a client that has not
 * implemented this still shows something a person can read.
 *
 * Deliberately narrower than markdown: `**bold**`, `_italic_`, `~~strike~~`,
 * `[label](https://…)`, and the two list forms. In particular `*single stars*`
 * are NOT italic, so "2 * 3 * 4" survives the editor unchanged.
 *
 * **THIS IS NOT THE MESSAGE RENDERER, and must never become it.** Reading is
 * `MessageMarkdown`, which runs the whole GFM subset this app ships — messages
 * also arrive from the candidate's client and carry whatever *its* renderer
 * allows, so anything narrower would silently drop formatting somebody else sent.
 *
 * This parser exists for exactly one job, and the narrowness is the qualification
 * rather than a shortcoming: `htmlMarkup.ts` turns markup into the editor's
 * contenteditable DOM and back, and it can only round-trip the marks the five
 * toolbar buttons can produce. A parser that understood more would hand the
 * editor nodes it has no way to serialise back out.
 *
 * So: two parsers in one client, on purpose. Pointing the thread at this one to
 * remove the duplication would make a bold-italic-strike-list renderer the app's
 * answer for everything else markdown can do.
 *
 * Note what this leaves alone. The collapsed one-line pill is a plain textarea
 * and sends exactly what was typed, so nothing on that path passes through here
 * at all — a recruiter typing "# of years?" into it gets a message the thread
 * reads as a heading and unwraps to "of years?", which is `MessageMarkdown`'s
 * allow-list doing its job and not this file's business.
 */

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; children: Inline[] }
  | { kind: 'italic'; children: Inline[] }
  | { kind: 'strike'; children: Inline[] }
  | { kind: 'link'; href: string; children: Inline[] };

/**
 * `paragraph` holds the lines of one run, not one line: pressing Enter in a
 * chat box is a line break, and only a blank line between them means a new
 * paragraph. Treating every line as its own paragraph put a gap under each one
 * and made a short list of times read like an essay.
 */
export type Block =
  { kind: 'paragraph'; lines: Inline[][] } | { kind: 'list'; ordered: boolean; items: Inline[][] };

type InlineMark = 'bold' | 'italic' | 'strike';

/** The token that opens and closes each inline mark. */
const MARKS: { token: string; kind: InlineMark }[] = [
  { token: '**', kind: 'bold' },
  { token: '~~', kind: 'strike' },
  { token: '_', kind: 'italic' },
];

const LINK = /\[([^\]\n]+)\]\(([^)\s]+)\)/;
const BULLET = /^[-*]\s+(.*)$/;
const ORDERED = /^\d+[.)]\s+(.*)$/;

/**
 * Only schemes a message can justify. Anything else (`javascript:`, a bare
 * word) renders as the literal text it was written as rather than becoming a
 * link, so a pasted string cannot turn into a navigation the reader did not
 * expect.
 */
function isSafeHref(href: string): boolean {
  return /^(https?:\/\/|mailto:)/i.test(href);
}

/** A formatting run found in the text, and where it starts. */
type Candidate =
  | { index: number; kind: 'link'; match: RegExpExecArray }
  | { index: number; kind: 'mark'; token: string; mark: InlineMark; close: number };

/** Split one line into its inline runs. Marks nest, so this recurses. */
export function parseInline(source: string): Inline[] {
  const out: Inline[] = [];

  const pushText = (text: string) => {
    if (text === '') return;
    const last = out[out.length - 1];
    // Merge, so an unmatched marker and the words either side stay one run.
    if (last && last.kind === 'text') last.text += text;
    else out.push({ kind: 'text', text });
  };

  let rest = source;
  while (rest !== '') {
    // The earliest thing that opens AND closes wins. A marker with no partner
    // is not a marker, so "5 ** 3" keeps its stars.
    const candidates: Candidate[] = [];

    const link = LINK.exec(rest);
    if (link) candidates.push({ index: link.index, kind: 'link', match: link });

    for (const { token, kind } of MARKS) {
      const open = rest.indexOf(token);
      if (open === -1) continue;
      const close = rest.indexOf(token, open + token.length);
      if (close === -1 || close === open + token.length) continue; // unmatched or empty
      candidates.push({ index: open, kind: 'mark', token, mark: kind, close });
    }

    if (candidates.length === 0) {
      pushText(rest);
      break;
    }

    const found = candidates.reduce((first, next) => (next.index < first.index ? next : first));
    pushText(rest.slice(0, found.index));

    if (found.kind === 'link') {
      const [whole, label, href] = found.match;
      if (isSafeHref(href!)) out.push({ kind: 'link', href: href!, children: parseInline(label!) });
      else pushText(whole);
      rest = rest.slice(found.index + whole.length);
    } else {
      const inner = rest.slice(found.index + found.token.length, found.close);
      out.push({ kind: found.mark, children: parseInline(inner) });
      rest = rest.slice(found.close + found.token.length);
    }
  }

  return out;
}

/**
 * Split a message into blocks: runs of plain lines into paragraphs, runs of
 * list lines into lists. A blank line ends whatever is open, which is the only
 * thing it does — no markdown "two trailing spaces" rule, because nobody types
 * that into a chat box.
 */
export function parseRichText(source: string): Block[] {
  const blocks: Block[] = [];
  // The run being built. Cleared by a blank line or by a line of another kind,
  // so "open" is never guessed from the end of `blocks`.
  let paragraph: Inline[][] | null = null;
  let list: { ordered: boolean; items: Inline[][] } | null = null;

  const closeAll = () => {
    paragraph = null;
    list = null;
  };

  for (const line of source.split('\n')) {
    const trimmed = line.trim();
    if (trimmed === '') {
      closeAll();
      continue;
    }

    const bullet = BULLET.exec(trimmed);
    const ordered = bullet ? null : ORDERED.exec(trimmed);

    if (bullet || ordered) {
      const isOrdered = bullet === null;
      const item = parseInline((bullet ?? ordered)![1]!);
      paragraph = null;
      // Switching kind starts a new list, so a bulleted run under a numbered
      // one stays two lists rather than one confused one.
      if (list && list.ordered === isOrdered) {
        list.items.push(item);
      } else {
        list = { ordered: isOrdered, items: [item] };
        blocks.push({ kind: 'list', ...list });
      }
      continue;
    }

    list = null;
    if (paragraph) {
      paragraph.push(parseInline(trimmed));
    } else {
      paragraph = [parseInline(trimmed)];
      blocks.push({ kind: 'paragraph', lines: paragraph });
    }
  }

  return blocks;
}
